/**
 * features/finance/BillingRun.tsx
 * ------------------------------------------------------------------
 * P2 — Billing run. Pick a year + term, preview every student's
 * invoice (tuition by form band, sibling discounts applied across
 * families, optional add-ons where opted in), then issue.
 *
 * Preview is computed by planBillingRun (pure engine). Issuing writes
 * one invoice per student with a deterministic id
 * (`${year}_${term}_${studentId}`), so re-running is safe: already
 * issued invoices are skipped and never clobbered. Students whose form
 * band has no fee structure for the year are flagged, not billed.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from "react";
import {
  Play,
  AlertTriangle,
  ReceiptText,
  Users,
  CheckCircle2,
  Plus,
  Trash2,
  Search,
  X,
  CalendarDays,
} from "lucide-react";
/* The accounting design kit, not components/ui/glass — this screen was the
   last finance page still on the old one and read as a different app. */
import {
  SoftCard,
  StatChip,
  AccentButton,
  glassFieldClass,
  GradientHero,
} from "@/features/finance/ui";
import { Spinner } from "@/components/ui/primitives";
import { SaveConfirmation } from "@/components/ui/SaveConfirmation";
import { useAuth } from "@/contexts/AuthContext";
import { useData, COLLECTIONS } from "@/contexts/DataContext";
import { db, updateDoc } from "@/lib/firebase";
import { doc } from "firebase/firestore";
import { useFinanceData } from "@/features/finance/useFinanceData";
import { finance } from "@/features/finance/financeWrite";
import { recordFinanceAudit } from "@/lib/financeAudit";
import {
  SCHOOL,
  termLabel,
  termDates,
  currentTermKey,
  termIndex,
  type TermKey,
} from "@/lib/school.config";
import {
  planBillingRun,
  runInvoiceId,
  isRunInvoiceId,
  bandForClass,
  formatInvoiceNo,
  fmtPula,
  buildInstalments,
  instalmentsFromPlan,
  resolveInstalmentRows,
  splitsToPlanRows,
  offsetDate,
  DEFAULT_INSTALMENT_PLAN,
  PREPAY_LABEL,
  feeCaseApproved,
  type InstalmentPlanRow,
  type BillingStudent,
} from "@/lib/financeCore";
import type { FamilyBillingPlan } from "@/types";
import {
  cohortByClassId, studentsInCohort, cohortCounts, COHORT_LABELS,
  type BillingCohort,
} from '@/lib/cohort';

const TERMS = Object.keys(SCHOOL.terms) as TermKey[];

export default function BillingRun() {
  const acad = useData();
  const data = useFinanceData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? "unknown", email: userData?.email };

  const [year, setYear] = useState(
    SCHOOL.selectableYears[0] ?? String(new Date().getFullYear()),
  );
  /* WHICH GROUP THIS RUN IS FOR.
     A-level takes an intake in January and another in June, and the June
     group's Term 2 is running while everybody else is in Term 3. A run that
     swept up every A-level pupil would charge one of the two groups for a
     term they are not in - and their instalments fall on different dates for
     the same reason, which is why the date below belongs to the run. */
  const [cohort, setCohort] = useState<BillingCohort>('main');
  const [term, setTerm] = useState<TermKey>(currentTermKey());
  const [anchorDate, setAnchorDate] = useState(() => parseTermStart(term, year));
  const [generalRows, setGeneralRows] = useState<InstalmentPlanRow[]>(() => {
    const s = data.settings;
    if (s.defaultInstalmentPlan && s.defaultInstalmentPlan.length > 0) return s.defaultInstalmentPlan;
    if (s.defaultInstalmentSplits && s.defaultInstalmentSplits.length > 0) return splitsToPlanRows(s.defaultInstalmentSplits);
    return DEFAULT_INSTALMENT_PLAN;
  });
  // Re-anchor the first instalment to the term's start when term/year change.
  useEffect(() => { setAnchorDate(parseTermStart(term, year)); }, [term, year]);
  const pctTotal = generalRows.reduce((a, r) => a + (Number(r.percent) || 0), 0);
  /* Staff children on an APPROVED link are not billed: the benefit is settled
     through payroll, not by raising an invoice and discounting it away. */
  const staffChildIds = useMemo(() => {
    const set = new Set<string>();
    for (const link of data.staffChildren) {
      if (!feeCaseApproved(link)) continue;
      for (const id of link.childStudentIds ?? []) set.add(id);
    }
    return set;
  }, [data.staffChildren]);

  /* Bursaries and full annual payment no longer come off here (the school, Nov
     2026): every discount is issued against an invoice from the Discounts screen,
     so the engine is deliberately given neither. */
  /* Sibling discount is withheld until a finance admin approves it; only
     families with an ACTIVE approved record get it applied at billing.

     RETIRED Aug 2026 — the automatic sibling rule no longer fires (see
     computeDiscounts). Still computed and still passed so an approval left
     over from the old scheme cannot change a bill by surprise, and so the
     shape of the engine input did not have to change under a live system. */
  const approvedSiblingFamilyIds = useMemo(
    () => new Set(data.appliedSiblingDiscounts.filter((a) => a.status === 'active').map((a) => a.familyId)),
    [data.appliedSiblingDiscounts],
  );

  /* NO DISCOUNTS ARE APPLIED HERE, and that is the school's chosen order of
     work: the billing run raises every invoice at the FULL fee, and the
     bursar discounts afterwards, pupil by pupil, from the Discounts screen.

     Which means a concession recorded against a pupil must NOT also come off
     at billing — it would be granted twice, once silently on the invoice and
     once again when the bursar applies it. So there is deliberately nothing
     wired in here. See features/finance/StudentDiscounts.tsx. */
  const [confirming, setConfirming] = useState(false);
  const [sc, setSc] = useState<{
    show: boolean;
    status: "saving" | "success" | "error";
    message: string;
  }>({ show: false, status: "saving", message: "" });

  const classById = useMemo(() => {
    const m: Record<string, (typeof acad.classes)[number]> = {};
    acad.classes.forEach((c) => { m[c.id] = c; });
    return m;
  }, [acad.classes]);
  const cohortOf = useMemo(() => cohortByClassId(acad.classes), [acad.classes]);
  /* Only a current pupil is billed: not one still in Pastel only ('pending'),
     and not one who has LEFT — a leaver keeps their last class, so on 8 Oct
     2026 AS JUNE showed 29 to invoice where the register has 17. */
  const isBillable = (s: { status?: string }) => s.status !== 'pending' && s.status !== 'left';
  const counts = useMemo(
    () => cohortCounts(acad.students.filter(isBillable), cohortOf),
    [acad.students, cohortOf],
  );

  const students: BillingStudent[] = useMemo(
    () =>
      studentsInCohort(acad.students, cohortOf, cohort)
        // SUSPENDERS: never bill a 'pending' (Pastel-only, not yet enrolled)
        // student. Without this a pending pupil with currentClass:'' defaults
        // to fee band F2_3 and would be silently invoiced.
        .filter(isBillable)
        .map((s) => ({
          id: s.id,
          name: s.fullName,
          familyId: s.familyId,
          /* The class, not just its form: AS and A2 may be priced apart, and
             so may the June intake. bandChain falls back to plain 'AL' for
             whichever the bursar has not set up. */
          bandKey: bandForClass(classById[s.currentClass ?? '']),
          pendingDevFee: s.pendingDevelopmentFee,
          status: s.status, // BELT: engine also refuses to bill status:'pending'
        })),
    [acad.students, classById, cohortOf, cohort],
  );

  /* A pupil already invoiced for this term's fees counts as invoiced however
     the invoice was made. The run recognises its own invoices by id
     (`${year}_${term}_${studentId}`); an invoice raised by hand on the
     Invoice screen has another id, so on 8 Oct 2026 the 17 new pupils the
     bursar had invoiced individually still showed as "to issue" — and
     issuing would have billed them twice. A hand-made invoice for the term
     that carries TUITION stands in for the run's own; one for exam fees or
     boarding alone does not. */
  const handInvoiced = useMemo(() => {
    const ids = new Set<string>();
    for (const i of data.invoices) {
      if (i.year !== year || i.term !== term || i.status === 'void') continue;
      if (i.kind === 'opening' || String(i.invoiceNo ?? '').startsWith('OPEN/')) continue;
      if (isRunInvoiceId(i.id, year, term, i.studentId)) continue; // the run's own
      if ((i.lines ?? []).some((l) => l.category === 'tuition')) ids.add(i.studentId);
    }
    return ids;
  }, [data.invoices, year, term]);

  /* Live and voided run invoices are kept apart: a voided one bills nobody,
     so its pupil can be billed again under a fresh id (financeCore
     runInvoiceId) and the voided record is left as it was. */
  const voidedInvoiceIds = useMemo(
    () => new Set(data.invoices.filter((i) => i.year === year && i.term === term && i.status === 'void').map((i) => i.id)),
    [data.invoices, year, term],
  );
  const existingInvoiceIds = useMemo(
    () => {
      const ids = new Set(
        data.invoices
          .filter((i) => i.year === year && i.term === term && i.status !== 'void')
          .map((i) => i.id),
      );
      // A hand-made tuition invoice stands in for whichever id the run would use.
      for (const sid of handInvoiced) ids.add(runInvoiceId(year, term, sid, voidedInvoiceIds));
      return ids;
    },
    [data.invoices, year, term, handInvoiced, voidedInvoiceIds],
  );

  const plan = useMemo(
    () =>
      planBillingRun({
        students,
        structures: data.feeStructures,
        rules: data.discountRules,
        settings: data.settings,
        existingInvoiceIds,
        voidedInvoiceIds,
        year,
        term,
        approvedSiblingFamilyIds,
        staffChildIds,
      }),
    [
      students,
      data.feeStructures,
      data.discountRules,
      data.settings,
      existingInvoiceIds,
      voidedInvoiceIds,
      year,
      term,
      approvedSiblingFamilyIds,
      staffChildIds,
    ],
  );

  if (acad.loading || data.loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner label="Loading…" />
      </div>
    );
  }

  const issue = async () => {
    const toCreate = plan.rows.filter(
      (r) => r.hasStructure && !r.alreadyInvoiced && !r.notBilledThisTerm,
    );
    if (toCreate.length === 0) return;
    setConfirming(false);
    setSc({
      show: true,
      status: "saving",
      message: `Issuing ${toCreate.length} invoices…`,
    });
    try {
      const issuedAt = new Date().toISOString();
      const anchorIso = new Date(`${anchorDate}T12:00:00`).toISOString();
      let seq = data.invoices.filter((i) => i.year === year).length + 1;
      const docs = toCreate.map((r) => {
        const fam = r.familyId ? acad.familyById(r.familyId) : undefined;
        const stu = acad.students.find((s) => s.id === r.studentId);
        let instalments;
        if (r.prepay) {
          instalments = r.total > 0
            ? buildInstalments(r.total, [100], [anchorIso.slice(0, 10)], [PREPAY_LABEL])
            : [];
        } else {
          const rows = resolveInstalmentRows({
            studentPlan: stu?.billingPlan,
            familyPlan: fam?.billingPlan,
            generalRows,
          });
          instalments = r.total > 0 ? instalmentsFromPlan(r.total, anchorIso, rows) : [];
        }
        const lastDue = instalments.length
          ? instalments[instalments.length - 1].dueDate
          : anchorIso.slice(0, 10);
        const dueIsoRow = new Date(`${lastDue}T12:00:00`).toISOString();
        return {
          id: r.invoiceId,
          invoiceNo: formatInvoiceNo(data.settings, { year, term, seq: seq++ }),
          studentId: r.studentId,
          studentName: r.studentName,
          familyId: r.familyId ?? null,
          term,
          /* Which calendar this term belongs to. `term` stays a TermKey so
             every guard and grouping still works; this is what lets the
             invoice say "Term 2 (June Intake)" to a family whose child is in
             their second term while the school is in its third. Omitted for
             the main school, where it would say nothing. */
          ...(cohort === 'main' ? {} : { cohort }),
          year,
          issuedAt,
          dueDate: dueIsoRow,
          ...(instalments.length > 1 ? { instalments } : {}),
          lines: r.lines,
          discounts: r.discounts,
          subtotal: r.subtotal,
          discountTotal: r.discountTotal,
          total: r.total,
          paid: 0,
          balance: r.total,
          status: r.total <= 0 ? "paid" : "issued",
        };
      });
      for (let i = 0; i < docs.length; i += 50) {
        await Promise.all(
          docs.slice(i, i + 50).map((d) => finance.invoices.set(d.id, d)),
        );
      }
      // Clear the one-shot development-fee marker on students who were just
      // billed for it, so it can never be charged a second time.
      const devFeeStudentIds = toCreate
        .filter((r) => (r.devFee ?? 0) > 0)
        .map((r) => r.studentId);
      for (let i = 0; i < devFeeStudentIds.length; i += 50) {
        await Promise.all(
          devFeeStudentIds.slice(i, i + 50).map((sid) =>
            updateDoc(doc(db, COLLECTIONS.students, sid), {
              pendingDevelopmentFee: null,
              updatedAt: new Date().toISOString(),
            }),
          ),
        );
      }
      await recordFinanceAudit({
        action: "billing.run",
        actor,
        subjectId: `${year}_${term}`,
        after: { issued: docs.length, net: plan.totals.net },
      });
      setSc({
        show: true,
        status: "success",
        message: `Issued ${docs.length} invoices for ${termLabel(term)} ${year}.`,
      });
    } catch (e) {
      console.error(e);
      setSc({
        show: true,
        status: "error",
        message:
          "Billing run failed — re-run to continue (issued invoices are skipped).",
      });
    }
  };

  const isCurrentYear =
    year === (SCHOOL.selectableYears[0] ?? String(new Date().getFullYear()));
  const isPastTerm =
    isCurrentYear && termIndex(term) < termIndex(currentTermKey());
  const nothingToIssue = plan.counts.toIssue === 0;

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Play}
        title="Billing"
        subtitle="Issue invoices for a term, at the full fee by form band. Discounts are issued afterwards."
      />
      <SaveConfirmation
        show={sc.show}
        status={sc.status}
        message={sc.message}
        onDismiss={() => setSc((s) => ({ ...s, show: false }))}
      />

      {/* Controls */}
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              Who this run bills
            </span>
            <select
              value={cohort}
              onChange={(e) => setCohort(e.target.value as BillingCohort)}
              className={glassFieldClass("py-2")}
            >
              {(['main', 'preSchool', 'alJan', 'alJune'] as BillingCohort[]).map((c) => (
                <option key={c} value={c}>
                  {COHORT_LABELS[c]} ({counts[c]})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              Term
            </span>
            <select
              value={term}
              onChange={(e) => setTerm(e.target.value as TermKey)}
              className={glassFieldClass("py-2")}
            >
              {TERMS.map((t) => (
                <option key={t} value={t}>
                  {termLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              Academic year
            </span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={glassFieldClass("py-2")}
            >
              {SCHOOL.selectableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
              First instalment date (term start)
            </span>
            <input
              type="date"
              value={anchorDate}
              onChange={(e) => setAnchorDate(e.target.value)}
              className={glassFieldClass("py-2")}
            />
          </label>
          <div className="flex-1" />
          <div className="text-right">
            <div className="text-xs text-gray-400">Net to bill this run</div>
            <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
              {fmtPula(plan.totals.net)}
            </div>
          </div>
        </div>
        <div className="mt-3 text-xs">
          {term === currentTermKey() ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              Current term
            </span>
          ) : isPastTerm ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
              Archived term — read only
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Future term
            </span>
          )}
        </div>
      </SoftCard>

      {!isPastTerm && (
        <>
          <SoftCard hover={false}>
            <div className="flex items-center gap-2 mb-3">
              <CalendarDays size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                General instalment schedule
              </h3>
              <span
                className={`ml-auto text-xs font-medium ${
                  pctTotal === 100
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-rose-600 dark:text-rose-400"
                }`}
              >
                {pctTotal}%{pctTotal === 100 ? "" : " — must total 100%"}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Everyone except the exceptions below is billed on this schedule.
              Due dates are relative to the first instalment date.
            </p>
            <ScheduleEditor
              rows={generalRows}
              anchorDate={anchorDate}
              onChange={setGeneralRows}
            />
          </SoftCard>

          <BillingExceptions anchorDate={anchorDate} generalRows={generalRows} />
        </>
      )}

      {isPastTerm && (
        <SoftCard hover={false}>
          <p className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-gray-400"
            />
            {termLabel(term)} {year} is a past term and is read-only — no new
            invoices can be issued into it. Outstanding balances from earlier
            terms are still collected under{" "}
            <span className="font-medium">Payments</span> and chased under{" "}
            <span className="font-medium">Arrears</span>.
          </p>
        </SoftCard>
      )}

      {/* Counts */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatChip
          tone="emerald"
          value={String(plan.counts.toIssue)}
          label="To issue"
          icon={ReceiptText}
        />
        <StatChip
          tone="slate"
          value={String(plan.counts.alreadyInvoiced)}
          label="Already invoiced"
          icon={CheckCircle2}
        />
        <StatChip
          tone={plan.counts.noStructure > 0 ? "red" : "slate"}
          value={String(plan.counts.noStructure)}
          label="No fee structure"
          icon={Users}
        />
      </div>

      {plan.counts.noStructure > 0 && (
        <SoftCard hover={false}>
          <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            {plan.counts.noStructure} student(s) have no fee structure for their
            form band in {year}. They won't be billed — add the missing
            structure(s) under Fee Setup, then preview again.
          </p>
        </SoftCard>
      )}

      {/* Per-band breakdown */}
      <SoftCard pad="p-0" hover={false}>
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">
            Preview — {termLabel(term)} {year}
          </h3>
        </div>
        <div className="p-5 overflow-x-auto">
          {plan.byBand.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nothing to issue.{" "}
              {plan.counts.alreadyInvoiced > 0
                ? "These students are already invoiced for this period."
                : "Set fee structures under Fee Setup first."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Form band</th>
                  <th className="py-2 px-3 font-medium text-right">Students</th>
                  <th className="py-2 px-3 font-medium text-right">Gross</th>
                  <th className="py-2 px-3 font-medium text-right">
                    Discounts
                  </th>
                  <th className="py-2 pl-3 font-medium text-right">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {plan.byBand.map((b) => (
                  <tr key={b.bandKey}>
                    <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">
                      {b.label}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                      {b.count}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                      {fmtPula(b.gross)}
                    </td>
                    <td className="py-2 px-3 text-right text-red-600 dark:text-red-400">
                      {b.discount > 0 ? `−${fmtPula(b.discount)}` : "—"}
                    </td>
                    <td className="py-2 pl-3 text-right font-semibold text-gray-800 dark:text-gray-100">
                      {fmtPula(b.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 dark:border-gray-600 font-semibold text-gray-800 dark:text-gray-100">
                  <td className="py-2 pr-3">Total</td>
                  <td className="py-2 px-3 text-right">
                    {plan.counts.toIssue}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {fmtPula(plan.totals.gross)}
                  </td>
                  <td className="py-2 px-3 text-right text-red-600 dark:text-red-400">
                    {plan.totals.discount > 0
                      ? `−${fmtPula(plan.totals.discount)}`
                      : "—"}
                  </td>
                  <td className="py-2 pl-3 text-right">
                    {fmtPula(plan.totals.net)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
          {handInvoiced.size > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              {handInvoiced.size} pupil{handInvoiced.size === 1 ? ' was' : 's were'} already invoiced by hand for {termLabel(term)} {year} and {handInvoiced.size === 1 ? 'is' : 'are'} counted as invoiced.
            </p>
          )}
          {/* Who exactly — so nobody has to work out which one was missed. */}
          {plan.counts.toIssue > 0 && (
            <details className="mt-4" open={plan.counts.toIssue <= 30}>
              <summary className="text-sm font-semibold text-gray-800 dark:text-gray-100 cursor-pointer">
                Who will be invoiced ({plan.counts.toIssue})
              </summary>
              <table className="w-full text-sm mt-2">
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {plan.rows
                    .filter((r) => r.hasStructure && !r.alreadyInvoiced && !r.notBilledThisTerm)
                    .sort((a, b) => a.studentName.localeCompare(b.studentName))
                    .map((r) => {
                      const stu = acad.students.find((s) => s.id === r.studentId);
                      return (
                        <tr key={r.studentId}>
                          <td className="py-1.5 pr-3 text-gray-800 dark:text-gray-100">{r.studentName}</td>
                          <td className="py-1.5 px-3 text-gray-500 dark:text-gray-400">{classById[stu?.currentClass ?? '']?.name ?? '—'}</td>
                          <td className="py-1.5 pl-3 text-right tabular-nums text-gray-700 dark:text-gray-200">{fmtPula(r.total)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </details>
          )}
        </div>
      </SoftCard>

      {/* Issue */}
      {!nothingToIssue && !isPastTerm && (
        <div className="flex items-center justify-end gap-3">
          {confirming ? (
            <>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Issue {plan.counts.toIssue} invoices for {termLabel(term)}{" "}
                {year}?
              </span>
              <button
                onClick={() => setConfirming(false)}
                className="px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <AccentButton icon={Play} onClick={issue}>
                Confirm &amp; issue
              </AccentButton>
            </>
          ) : (
            <AccentButton icon={Play} onClick={() => setConfirming(true)}>
              Issue billing run
            </AccentButton>
          )}
        </div>
      )}
    </div>
  );
}

/* ----------------------- instalment schedule editor ----------------------- */

function ScheduleEditor({
  rows,
  anchorDate,
  onChange,
}: {
  rows: InstalmentPlanRow[];
  anchorDate: string;
  onChange: (rows: InstalmentPlanRow[]) => void;
}) {
  const anchorIso = `${anchorDate}T12:00:00`;
  const set = (i: number, patch: Partial<InstalmentPlanRow>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const add = () =>
    onChange([
      ...rows,
      { percent: 0, offsetDays: (rows[rows.length - 1]?.offsetDays ?? 0) + 30 },
    ]);
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500 w-14 shrink-0">
            Inst. {i + 1}
          </span>
          <input
            type="number"
            min={0}
            max={100}
            value={r.percent}
            onChange={(e) => set(i, { percent: Number(e.target.value) })}
            className={glassFieldClass("py-1 w-20 text-sm")}
          />
          <span className="text-xs text-gray-400">%</span>
          <span className="text-xs text-gray-400 ml-2">due</span>
          {i === 0 ? (
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {anchorDate} (start)
            </span>
          ) : (
            <>
              <span className="text-xs text-gray-400">+</span>
              <input
                type="number"
                min={0}
                value={r.offsetDays}
                onChange={(e) => set(i, { offsetDays: Number(e.target.value) })}
                className={glassFieldClass("py-1 w-20 text-sm")}
              />
              <span className="text-xs text-gray-400">
                days → {offsetDate(anchorIso, r.offsetDays)}
              </span>
            </>
          )}
          {rows.length > 1 && (
            <button
              onClick={() => remove(i)}
              className="ml-auto text-gray-400 hover:text-rose-600"
              aria-label="Remove instalment"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={add}
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
      >
        <Plus size={13} /> Add instalment
      </button>
    </div>
  );
}

/* ------------------------ per-family/student exceptions ------------------------ */

function hasCustomPlan(plan?: FamilyBillingPlan): boolean {
  return !!plan && (
    (plan.instalments?.length ?? 0) > 0 ||
    (plan.splits?.length ?? 0) > 0 ||
    plan.kind === "prepayYear"
  );
}

function BillingExceptions({
  anchorDate,
  generalRows,
}: {
  anchorDate: string;
  generalRows: InstalmentPlanRow[];
}) {
  const acad = useData();
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<{
    kind: "family" | "student";
    id: string;
    name: string;
    rows: InstalmentPlanRow[];
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const exceptions = useMemo(() => {
    const fam = acad.families
      .filter((f) => hasCustomPlan(f.billingPlan))
      .map((f) => ({ kind: "family" as const, id: f.id, name: f.name, plan: f.billingPlan! }));
    const stu = acad.students
      .filter((s) => hasCustomPlan(s.billingPlan))
      .map((s) => ({ kind: "student" as const, id: s.id, name: s.fullName, plan: s.billingPlan! }));
    return [...fam, ...stu];
  }, [acad.families, acad.students]);

  const q = query.trim().toLowerCase();
  const famMatches = q
    ? acad.families.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 5)
    : [];
  const stuMatches = q
    ? acad.students
        .filter(
          (s) =>
            s.fullName.toLowerCase().includes(q) ||
            (s.admissionNumber ?? "").toLowerCase().includes(q),
        )
        .slice(0, 5)
    : [];

  const rowsFromPlan = (plan?: FamilyBillingPlan): InstalmentPlanRow[] => {
    if (plan?.instalments?.length) return plan.instalments.map((r) => ({ ...r }));
    if (plan?.splits?.length) return splitsToPlanRows(plan.splits);
    return generalRows.map((r) => ({ ...r }));
  };

  const open = (kind: "family" | "student", id: string, name: string, plan?: FamilyBillingPlan) => {
    setEditing({ kind, id, name, rows: rowsFromPlan(plan) });
    setQuery("");
  };

  const pctTotal = editing ? editing.rows.reduce((a, r) => a + (Number(r.percent) || 0), 0) : 0;

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const plan: FamilyBillingPlan = { kind: "custom", instalments: editing.rows };
      const col = editing.kind === "family" ? COLLECTIONS.families : COLLECTIONS.students;
      await updateDoc(doc(db, col, editing.id), {
        billingPlan: plan,
        updatedAt: new Date().toISOString(),
      });
      setEditing(null);
    } finally {
      setBusy(false);
    }
  };

  const clear = async (kind: "family" | "student", id: string) => {
    setBusy(true);
    try {
      const col = kind === "family" ? COLLECTIONS.families : COLLECTIONS.students;
      await updateDoc(doc(db, col, id), {
        billingPlan: null,
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SoftCard hover={false}>
      <div className="flex items-center gap-2 mb-1">
        <Users size={16} className="text-primary" />
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          Different arrangements
        </h3>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Families or students who pay on their own schedule. Saved on the record,
        so it applies automatically every term until you remove it. A student's
        own plan overrides their family's.
      </p>

      {/* search + add */}
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a family or student to add…"
          className={glassFieldClass("py-2 pl-9 w-full text-sm")}
        />
        {q && (famMatches.length > 0 || stuMatches.length > 0) && (
          <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg max-h-64 overflow-y-auto">
            {famMatches.map((f) => (
              <button
                key={`f_${f.id}`}
                onClick={() => open("family", f.id, f.name, f.billingPlan)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Users size={13} className="text-gray-400" /> {f.name}
                <span className="ml-auto text-[11px] text-gray-400">family</span>
              </button>
            ))}
            {stuMatches.map((s) => (
              <button
                key={`s_${s.id}`}
                onClick={() => open("student", s.id, s.fullName, s.billingPlan)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ReceiptText size={13} className="text-gray-400" /> {s.fullName}
                <span className="ml-auto text-[11px] text-gray-400">
                  {s.admissionNumber ?? "student"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* current exceptions */}
      {exceptions.length === 0 ? (
        <p className="text-xs text-gray-400">No exceptions — everyone is on the general schedule.</p>
      ) : (
        <div className="space-y-1.5">
          {exceptions.map((ex) => (
            <div
              key={`${ex.kind}_${ex.id}`}
              className="flex items-center gap-2 rounded-lg border border-gray-100 dark:border-gray-700 px-3 py-2"
            >
              {ex.kind === "family" ? (
                <Users size={13} className="text-gray-400 shrink-0" />
              ) : (
                <ReceiptText size={13} className="text-gray-400 shrink-0" />
              )}
              <span className="text-sm text-gray-800 dark:text-gray-100 truncate">{ex.name}</span>
              <span className="text-[11px] text-gray-400">
                {ex.plan.kind === "prepayYear"
                  ? "prepay year"
                  : `${(ex.plan.instalments?.length ?? ex.plan.splits?.length ?? 0)} parts`}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => open(ex.kind, ex.id, ex.name, ex.plan)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Edit
                </button>
                <button
                  onClick={() => clear(ex.kind, ex.id)}
                  disabled={busy}
                  className="text-gray-400 hover:text-rose-600 disabled:opacity-40"
                  aria-label="Remove exception"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* editor modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => !busy && setEditing(null)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Schedule for {editing.name}
              </h4>
              <button
                onClick={() => !busy && setEditing(null)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Percentages must total 100%. Due dates are relative to the term's
              first instalment date.
            </p>
            <ScheduleEditor
              rows={editing.rows}
              anchorDate={anchorDate}
              onChange={(rows) => setEditing((e) => (e ? { ...e, rows } : e))}
            />
            <div className="mt-4 flex items-center gap-2">
              <span
                className={`text-xs font-medium ${
                  pctTotal === 100
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-rose-600 dark:text-rose-400"
                }`}
              >
                {pctTotal}%{pctTotal === 100 ? "" : " — must total 100%"}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  onClick={() => setEditing(null)}
                  disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={busy || pctTotal !== 100}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#1b3a6b] hover:bg-[#2d5295] disabled:opacity-50"
                >
                  {busy ? "Saving…" : "Save arrangement"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SoftCard>
  );
}

/* Best-effort parse of a term's first day from its display string
 * (e.g. "13 January – 17 April") + the selected year → YYYY-MM-DD.
 * Falls back to today if the string can't be parsed. */
function parseTermStart(term: string, year: string): string {
  const today = new Date().toISOString().slice(0, 10);
  try {
    const left = termDates(term).split(/[–—-]/)[0]?.trim() ?? "";
    const m = left.match(/(\d{1,2})\s+([A-Za-z]+)/);
    if (!m) return today;
    const months = [
      "january", "february", "march", "april", "may", "june",
      "july", "august", "september", "october", "november", "december",
    ];
    const day = parseInt(m[1], 10);
    const mon = months.indexOf(m[2].toLowerCase());
    const yr = parseInt(year, 10);
    if (mon < 0 || !Number.isFinite(yr) || !Number.isFinite(day)) return today;
    const d = new Date(yr, mon, day, 12, 0, 0);
    return d.toISOString().slice(0, 10);
  } catch {
    return today;
  }
}
